// A tiny TrueType font with one blank glyph, for a PDF's invisible text layer: every character
// is drawn with it (so nothing shows), and the PDF maps each one back to Unicode. Readers such as
// Preview only pick up selectable text from a real font, which a hand-made one this small is.

/** Each glyph's advance, in font units (1000 to the em). */
export const BLANK_ADVANCE = 500;

function table(size: number, fill: (v: DataView) => void): Uint8Array {
  const bytes = new Uint8Array(size);
  fill(new DataView(bytes.buffer));
  return bytes;
}

function checksum(bytes: Uint8Array): number {
  const padded = new Uint8Array(Math.ceil(bytes.length / 4) * 4);
  padded.set(bytes);
  const v = new DataView(padded.buffer);
  let sum = 0;
  for (let i = 0; i < padded.length; i += 4) sum = (sum + v.getUint32(i)) >>> 0;
  return sum;
}

function utf16be(s: string): Uint8Array {
  const out = new Uint8Array(s.length * 2);
  for (let i = 0; i < s.length; i++) {
    out[i * 2] = s.charCodeAt(i) >> 8;
    out[i * 2 + 1] = s.charCodeAt(i) & 0xff;
  }
  return out;
}

/** The font file: two glyphs (.notdef and a blank one), both empty and BLANK_ADVANCE wide. */
export function blankFont(name = 'RelationshipsText'): Uint8Array {
  const glyphs = 2;
  const head = table(54, (v) => {
    v.setUint32(0, 0x00010000); // version
    v.setUint32(4, 0x00010000); // font revision
    v.setUint32(8, 0); // checksum adjustment, set below
    v.setUint32(12, 0x5f0f3cf5); // magic
    v.setUint16(16, 0x000b); // flags
    v.setUint16(18, 1000); // units per em
    // created and modified (8 bytes each) stay zero
    v.setInt16(36, 0);
    v.setInt16(38, -200);
    v.setInt16(40, BLANK_ADVANCE);
    v.setInt16(42, 800); // bounding box
    v.setUint16(46, 8); // smallest readable size
    v.setInt16(48, 2); // direction hint
    v.setInt16(50, 0); // short loca offsets
    v.setInt16(52, 0);
  });
  const hhea = table(36, (v) => {
    v.setUint32(0, 0x00010000);
    v.setInt16(4, 800); // ascender
    v.setInt16(6, -200); // descender
    v.setUint16(10, BLANK_ADVANCE); // widest advance
    v.setInt16(18, 1); // caret slope rise
    v.setUint16(34, glyphs); // horizontal metrics
  });
  const maxp = table(32, (v) => {
    v.setUint32(0, 0x00010000);
    v.setUint16(4, glyphs);
    v.setUint16(14, 2); // zones
  });
  const hmtx = table(glyphs * 4, (v) => {
    for (let i = 0; i < glyphs; i++) v.setUint16(i * 4, BLANK_ADVANCE);
  });
  const loca = new Uint8Array((glyphs + 1) * 2); // every glyph starts and ends at 0: no outlines
  const glyf = new Uint8Array(4);
  // A cmap with nothing in it but the required closing segment.
  const cmap = table(12 + 24, (v) => {
    v.setUint16(2, 1); // one subtable
    v.setUint16(4, 3);
    v.setUint16(6, 1); // Windows, Unicode BMP
    v.setUint32(8, 12);
    v.setUint16(12, 4); // format 4
    v.setUint16(14, 24); // length
    v.setUint16(18, 2); // segments × 2
    v.setUint16(20, 2); // search range
    v.setUint16(24, 0); // range shift
    v.setUint16(26, 0xffff); // end code
    v.setUint16(30, 0xffff); // start code
    v.setInt16(32, 1); // delta
  });
  const names: [number, string][] = [
    [1, name],
    [2, 'Regular'],
    [4, name],
    [6, name],
  ];
  const strings = names.map(([, s]) => utf16be(s));
  const nameTable = new Uint8Array(6 + names.length * 12 + strings.reduce((n, s) => n + s.length, 0));
  {
    const v = new DataView(nameTable.buffer);
    v.setUint16(2, names.length);
    v.setUint16(4, 6 + names.length * 12);
    let at = 0;
    names.forEach(([id], i) => {
      const r = 6 + i * 12;
      v.setUint16(r, 3);
      v.setUint16(r + 2, 1);
      v.setUint16(r + 4, 0x409);
      v.setUint16(r + 6, id);
      v.setUint16(r + 8, strings[i].length);
      v.setUint16(r + 10, at);
      nameTable.set(strings[i], 6 + names.length * 12 + at);
      at += strings[i].length;
    });
  }
  const post = table(32, (v) => {
    v.setUint32(0, 0x00030000); // no glyph names
    v.setInt16(8, -100);
    v.setInt16(10, 50);
  });
  const os2 = table(96, (v) => {
    v.setUint16(0, 4);
    v.setInt16(2, BLANK_ADVANCE);
    v.setUint16(4, 400); // weight
    v.setUint16(6, 5); // width
    v.setUint8(58, 0x4e);
    v.setUint8(59, 0x4f);
    v.setUint8(60, 0x4e);
    v.setUint8(61, 0x45); // vendor "NONE"
    v.setUint16(62, 0x40); // regular
    v.setUint16(64, 0x20);
    v.setUint16(66, 0xffff); // first and last characters
    v.setInt16(68, 800);
    v.setInt16(70, -200); // typographic ascender and descender
    v.setUint16(74, 800);
    v.setUint16(76, 200); // Windows ascent and descent
    v.setUint16(90, 32); // break character
  });

  return assemble([
    ['OS/2', os2],
    ['cmap', cmap],
    ['glyf', glyf],
    ['head', head],
    ['hhea', hhea],
    ['hmtx', hmtx],
    ['loca', loca],
    ['maxp', maxp],
    ['name', nameTable],
    ['post', post],
  ]);
}

// ---------------------------------------------------------------- reading and subsetting

/** A TrueType font, read enough to set text in a PDF: which glyph draws each character, how wide it is, and a cut-down copy to embed. */
export interface Font {
  unitsPerEm: number;
  ascent: number;
  descent: number;
  /** The glyph for a character, or 0 (.notdef) when the font hasn't got one. */
  glyphOf(codePoint: number): number;
  /** A glyph's advance width, in font units. */
  advance(glyph: number): number;
  /** The font with only these glyphs drawn (and the ones they're built from); glyph ids stay the same. */
  subset(glyphs: Iterable<number>): Uint8Array;
  /** A glyph's outline as PDF path operators, in font units with y up (fill it nonzero). */
  outline(glyph: number): string;
}

export function parseFont(bytes: Uint8Array): Font {
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tables = new Map<string, { at: number; length: number }>();
  const count = v.getUint16(4);
  for (let i = 0; i < count; i++) {
    const r = 12 + i * 16;
    tables.set(String.fromCharCode(...bytes.subarray(r, r + 4)), { at: v.getUint32(r + 8), length: v.getUint32(r + 12) });
  }
  const need = (tag: string) => {
    const t = tables.get(tag);
    if (!t) throw new Error(`The font has no ${tag} table`);
    return t;
  };
  const head = need('head');
  const unitsPerEm = v.getUint16(head.at + 18);
  const longLoca = v.getInt16(head.at + 50) === 1;
  const hhea = need('hhea');
  const ascent = v.getInt16(hhea.at + 4);
  const descent = v.getInt16(hhea.at + 6);
  const metrics = v.getUint16(hhea.at + 34);
  const glyphCount = v.getUint16(need('maxp').at + 4);
  const hmtx = need('hmtx');
  const loca = need('loca');
  const glyf = need('glyf');
  const glyphStart = (g: number) => (longLoca ? v.getUint32(loca.at + g * 4) : v.getUint16(loca.at + g * 2) * 2);

  // Characters to glyphs: a format 12 table (all of Unicode) if there is one, else format 4 (the first 65,536).
  const cmap = need('cmap');
  const groups: [number, number, number][] = [];
  let format4: number | undefined;
  for (let i = 0; i < v.getUint16(cmap.at + 2); i++) {
    const r = cmap.at + 4 + i * 8;
    const platform = v.getUint16(r);
    const encoding = v.getUint16(r + 2);
    const sub = cmap.at + v.getUint32(r + 4);
    const format = v.getUint16(sub);
    if (format === 12 && !groups.length && (platform === 3 || platform === 0)) {
      const n = v.getUint32(sub + 12);
      for (let k = 0; k < n; k++) {
        const g = sub + 16 + k * 12;
        groups.push([v.getUint32(g), v.getUint32(g + 4), v.getUint32(g + 8)]);
      }
    } else if (format === 4 && format4 === undefined && (platform === 0 || (platform === 3 && encoding === 1))) format4 = sub;
  }
  const fromFormat4 = (sub: number, c: number): number => {
    const segX2 = v.getUint16(sub + 6);
    const ends = sub + 14;
    const starts = ends + segX2 + 2;
    const deltas = starts + segX2;
    const ranges = deltas + segX2;
    for (let i = 0; i < segX2 / 2; i++) {
      if (c > v.getUint16(ends + i * 2)) continue;
      const start = v.getUint16(starts + i * 2);
      if (c < start) return 0;
      const delta = v.getInt16(deltas + i * 2);
      const range = v.getUint16(ranges + i * 2);
      if (!range) return (c + delta) & 0xffff;
      const g = v.getUint16(ranges + i * 2 + range + (c - start) * 2);
      return g ? (g + delta) & 0xffff : 0;
    }
    return 0;
  };
  const glyphOf = (c: number): number => {
    if (groups.length) {
      let lo = 0;
      let hi = groups.length - 1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const [start, end, first] = groups[mid];
        if (c < start) hi = mid - 1;
        else if (c > end) lo = mid + 1;
        else return first + (c - start);
      }
      return 0;
    }
    return format4 !== undefined && c <= 0xffff ? fromFormat4(format4, c) : 0;
  };
  const advance = (g: number) => v.getUint16(hmtx.at + Math.min(g, metrics - 1) * 4);

  /** The glyphs a composite glyph is built from. */
  const components = (g: number): number[] => {
    const at = glyf.at + glyphStart(g);
    if (glyphStart(g + 1) === glyphStart(g) || v.getInt16(at) >= 0) return [];
    const out: number[] = [];
    let p = at + 10;
    for (;;) {
      const flags = v.getUint16(p);
      out.push(v.getUint16(p + 2));
      p += 4 + (flags & 1 ? 4 : 2) + (flags & 8 ? 2 : flags & 0x40 ? 4 : flags & 0x80 ? 8 : 0);
      if (!(flags & 0x20)) return out;
    }
  };

  const subset = (wanted: Iterable<number>): Uint8Array => {
    const keep = new Set<number>([0]);
    const queue = [...wanted].filter((g) => g < glyphCount);
    while (queue.length) {
      const g = queue.pop()!;
      if (keep.has(g)) continue;
      keep.add(g);
      queue.push(...components(g));
    }
    // Same glyph ids; the ones not kept are empty. Offsets go in the long loca format.
    const pieces: Uint8Array[] = [];
    const newLoca = new Uint8Array((glyphCount + 1) * 4);
    const lv = new DataView(newLoca.buffer);
    let at = 0;
    for (let g = 0; g < glyphCount; g++) {
      lv.setUint32(g * 4, at);
      if (!keep.has(g)) continue;
      const data = bytes.subarray(glyf.at + glyphStart(g), glyf.at + glyphStart(g + 1));
      const padded = new Uint8Array(Math.ceil(data.length / 4) * 4);
      padded.set(data);
      pieces.push(padded);
      at += padded.length;
    }
    lv.setUint32(glyphCount * 4, at);
    const newGlyf = new Uint8Array(at);
    let o = 0;
    for (const piece of pieces) {
      newGlyf.set(piece, o);
      o += piece.length;
    }
    const copy = (tag: string) => {
      const t = need(tag);
      return bytes.slice(t.at, t.at + t.length);
    };
    const newHead = copy('head');
    new DataView(newHead.buffer).setInt16(50, 1);
    // Glyph names aren't needed to draw, so the post table drops them (version 3).
    const post = copy('post').slice(0, 32);
    new DataView(post.buffer).setUint32(0, 0x00030000);
    const out: [string, Uint8Array][] = [['cmap', copy('cmap')], ['glyf', newGlyf], ['head', newHead], ['hhea', copy('hhea')], ['hmtx', copy('hmtx')], ['loca', newLoca], ['maxp', copy('maxp')], ['name', copy('name')], ['post', post]];
    if (tables.has('OS/2')) out.unshift(['OS/2', copy('OS/2')]);
    return assemble(out);
  };

  /** A glyph's contours as points (x, y, on the curve or not), moved by (dx, dy). */
  const contours = (g: number, dx = 0, dy = 0, depth = 0): { x: number; y: number; on: boolean }[][] => {
    const start = glyphStart(g);
    if (g >= glyphCount || glyphStart(g + 1) === start || depth > 8) return [];
    const at = glyf.at + start;
    const count = v.getInt16(at);
    if (count < 0) {
      // Built from other glyphs, each moved by an offset (scaling is rare in text faces and is left out).
      const out: { x: number; y: number; on: boolean }[][] = [];
      let p = at + 10;
      for (;;) {
        const flags = v.getUint16(p);
        const part = v.getUint16(p + 2);
        const words = flags & 1;
        const ox = words ? v.getInt16(p + 4) : v.getInt8(p + 4);
        const oy = words ? v.getInt16(p + 6) : v.getInt8(p + 5);
        out.push(...contours(part, dx + (flags & 2 ? ox : 0), dy + (flags & 2 ? oy : 0), depth + 1));
        p += 4 + (words ? 4 : 2) + (flags & 8 ? 2 : flags & 0x40 ? 4 : flags & 0x80 ? 8 : 0);
        if (!(flags & 0x20)) return out;
      }
    }
    const ends: number[] = [];
    for (let i = 0; i < count; i++) ends.push(v.getUint16(at + 10 + i * 2));
    const points = count ? ends[count - 1] + 1 : 0;
    let p = at + 10 + count * 2;
    p += 2 + v.getUint16(p); // instructions
    const flags: number[] = [];
    while (flags.length < points) {
      const f = v.getUint8(p++);
      flags.push(f);
      if (f & 8) for (let r = v.getUint8(p++); r > 0; r--) flags.push(f);
    }
    const coord = (short: number, same: number) => {
      const out: number[] = [];
      let c = 0;
      for (const f of flags) {
        if (f & short) {
          const d = v.getUint8(p++);
          c += f & same ? d : -d;
        } else if (!(f & same)) {
          c += v.getInt16(p);
          p += 2;
        }
        out.push(c);
      }
      return out;
    };
    const xs = coord(2, 16);
    const ys = coord(4, 32);
    const out: { x: number; y: number; on: boolean }[][] = [];
    let from = 0;
    for (const end of ends) {
      const c: { x: number; y: number; on: boolean }[] = [];
      for (let i = from; i <= end; i++) c.push({ x: xs[i] + dx, y: ys[i] + dy, on: !!(flags[i] & 1) });
      out.push(c);
      from = end + 1;
    }
    return out;
  };

  const outline = (g: number): string => {
    const f = (v: number) => Number(v.toFixed(1)).toString();
    const ops: string[] = [];
    for (const c of contours(g)) {
      if (!c.length) continue;
      // Start on a point that's on the curve (or midway between two that aren't).
      const first = c.findIndex((pt) => pt.on);
      const pts = first >= 0 ? [...c.slice(first), ...c.slice(0, first)] : c;
      const startPt = first >= 0 ? pts[0] : { x: (c[0].x + c[c.length - 1].x) / 2, y: (c[0].y + c[c.length - 1].y) / 2, on: true };
      ops.push(`${f(startPt.x)} ${f(startPt.y)} m`);
      let cur = startPt;
      let ctrl: { x: number; y: number } | undefined;
      // Quadratic curves become cubic ones: control points two thirds of the way to the quadratic one.
      const curveTo = (q: { x: number; y: number }, to: { x: number; y: number }) => {
        ops.push(`${f(cur.x + (2 / 3) * (q.x - cur.x))} ${f(cur.y + (2 / 3) * (q.y - cur.y))} ${f(to.x + (2 / 3) * (q.x - to.x))} ${f(to.y + (2 / 3) * (q.y - to.y))} ${f(to.x)} ${f(to.y)} c`);
      };
      for (const pt of [...pts.slice(first >= 0 ? 1 : 0), startPt]) {
        if (pt.on) {
          if (ctrl) curveTo(ctrl, pt);
          else ops.push(`${f(pt.x)} ${f(pt.y)} l`);
          ctrl = undefined;
        } else if (ctrl) {
          const mid = { x: (ctrl.x + pt.x) / 2, y: (ctrl.y + pt.y) / 2 };
          curveTo(ctrl, mid);
          cur = { ...mid, on: true };
          ctrl = pt;
          continue;
        } else ctrl = pt;
        if (pt.on) cur = pt;
      }
      ops.push('h');
    }
    return ops.join(' ');
  };

  return { unitsPerEm, ascent, descent, glyphOf, advance, subset, outline };
}

/** Tables (in tag order) into a font file, with checksums and the head table's adjustment. */
function assemble(tables: [string, Uint8Array][]): Uint8Array {
  const dirSize = 12 + tables.length * 16;
  const total = dirSize + tables.reduce((n, [, t]) => n + Math.ceil(t.length / 4) * 4, 0);
  const font = new Uint8Array(total);
  const v = new DataView(font.buffer);
  const pow = 2 ** Math.floor(Math.log2(tables.length));
  v.setUint32(0, 0x00010000);
  v.setUint16(4, tables.length);
  v.setUint16(6, pow * 16);
  v.setUint16(8, Math.log2(pow));
  v.setUint16(10, tables.length * 16 - pow * 16);
  let at = dirSize;
  let headAt = -1;
  tables.forEach(([tag, t], i) => {
    const r = 12 + i * 16;
    for (let k = 0; k < 4; k++) font[r + k] = tag.charCodeAt(k);
    if (tag === 'head') {
      t = t.slice();
      new DataView(t.buffer).setUint32(8, 0);
      headAt = at;
    }
    v.setUint32(r + 4, checksum(t));
    v.setUint32(r + 8, at);
    v.setUint32(r + 12, t.length);
    font.set(t, at);
    at += Math.ceil(t.length / 4) * 4;
  });
  if (headAt >= 0) v.setUint32(headAt + 8, (0xb1b0afba - checksum(font)) >>> 0);
  return font;
}
